import { Breadcrumb as FuseBreadcrumb } from "@tvg/fuse/breadcrumb";
import { GridItem, GridRow } from "@tvg/fuse/grid";
import { parsePLPURL, PLPURL } from "@tvg/product-listing-url";
import useGlobalConfig from "client/context/GlobalConfig";
import useSEOConfig from "client/context/SEOContext";
import { createBreadcrumbLinks } from "client/utils/breadcrumb";
import React, { useMemo } from "react";
import styles from "./Breadcrumb.module.css";
import useLastNonEmptyTrail from "./useLastNonEmptyTrail";

// Home on its own is not a trail, it just repeats the logo
const MIN_CRUMBS = 2;

const Breadcrumb = () => {
  const { brand, isBreadcrumbsEnabled, origin } = useGlobalConfig();
  const { fallback, path, unfilteredFacet } = useSEOConfig();

  // the URL is what says where the shopper is - the response only fills in the names. Parsing the
  // same path the server rendered keeps SSR and the first client render in agreement
  const url = useMemo(() => {
    if (!path) return null;

    let parsed: PLPURL;
    try {
      parsed = parsePLPURL(path, brand);
    } catch {
      return null;
    }
    // the unfiltered facet can arrive in the query string, which the path on its own does not
    // carry. The route parsed the whole URL, so take its answer and keep the brand crumb reading
    // from the same source as the heading
    if (!parsed.unfilteredFacet && unfilteredFacet) {
      parsed.unfilteredFacet = unfilteredFacet;
    }

    return parsed;
  }, [path, brand, unfilteredFacet]);

  const links = useMemo(() => (url ? createBreadcrumbLinks(url, fallback) : []), [url, fallback]);
  const trail = useLastNonEmptyTrail(links, url?.category?.id ?? url?.pathname);

  if (!isBreadcrumbsEnabled || trail.length < MIN_CRUMBS) {
    return null;
  }

  // the row lives here rather than in SEOHeading so a switched-off flag renders literally nothing
  // and the heading does not shift down by an empty row
  return (
    <GridRow className="row">
      <GridItem xxs={12} l={12} className="grid-item">
        <FuseBreadcrumb links={trail} schemaOrigin={origin} className={`fuse-breadcrumb ${styles.breadcrumb}`} />
      </GridItem>
    </GridRow>
  );
};

export default React.memo(Breadcrumb);
